import type { ReactElement } from 'react';
import { formatDate } from '@xdocs/i18n';
import type { JobLog, JobStatus } from '@xdocs/schemas';
import { Badge, Button, EmptyState, Mascot, Skeleton, cn, useI18n } from '@xdocs/ui';
import type { BadgeTone } from '@xdocs/ui';
import { stagesForJobType } from './pipelineStages.js';
import { useJobSnapshot } from './useJobSnapshot.js';
import { useJobsT } from './useJobsT.js';
import styles from './JobProgressCard.module.css';

export interface JobProgressCardProps {
  jobId: string;
  /** `hero` (mockup 19/20 "Generating your document…" full-width moment
   * right after a generation/enhancement starts — mascot + bigger heading)
   * vs the default `inline` compact card (export modal, docked panels).
   * Same data/states either way — purely a presentational variant (E4:
   * "extend a component if the mockup demands a variant"). */
  variant?: 'inline' | 'hero';
  className?: string | undefined;
}

const STATUS_TONE: Record<JobStatus, BadgeTone> = {
  queued: 'neutral',
  running: 'info',
  succeeded: 'success',
  failed: 'error',
  canceled: 'neutral',
};

type StepState = 'done' | 'running' | 'queued' | 'error';

function logLevelForRow(log: JobLog): string {
  return log.level;
}

/**
 * The Job Progress surface (F14/E11 §0.2, CLAUDE_DESIGN.md §6 "Job Progress
 * surface"), rendered inline (also reusable as-is for a future notifications-
 * tray row / toast per the same spec — this instance is the inline variant).
 * States: loading (skeleton) · error (retry) · populated (stepper + live
 * log stream) · failed (attempt count + auto-retry-or-terminal messaging,
 * E11 §6 — no user-initiated retry op exists at M1, retries are automatic).
 */
export function JobProgressCard({ jobId, variant = 'inline', className }: JobProgressCardProps): ReactElement {
  const { locale } = useI18n();
  const { t } = useJobsT();
  const {
    snapshot,
    isLoading,
    isError,
    refetch,
    logs,
    loadEarlierLogs,
    isLoadingEarlierLogs,
    earlierLogsError,
    hasMoreEarlierLogs,
  } = useJobSnapshot(jobId);

  if (isError) {
    return (
      <div className={cn(styles['card'], className)}>
        <EmptyState kind="error" title={t('jobs:error.title')} action={{ label: t('jobs:error.retry'), onClick: refetch }} />
      </div>
    );
  }

  if (isLoading || !snapshot) {
    return (
      <div className={cn(styles['card'], className)} aria-busy="true">
        <div className={styles['skeletonBlock']}>
          <Skeleton shape="text-line" width="40%" />
          <Skeleton shape="row" count={4} />
        </div>
      </div>
    );
  }

  const { job, checkpoints } = snapshot;
  const doneStages = new Set(checkpoints.map((c) => c.stage));
  const stages = stagesForJobType(job.type, job.currentCheckpoint);
  const attemptsExhausted = job.status === 'failed' && job.attempts >= job.maxAttempts;

  return (
    <div className={cn(styles['card'], variant === 'hero' && styles['hero'], className)} data-job-status={job.status}>
      {variant === 'hero' && (
        <div className={styles['heroHeader']}>
          <Mascot pose="thinking" size={80} animate alt="" />
          <div>
            <h2 className={styles['heroTitle']}>{t(`jobs:hero.title.${job.status}` as Parameters<typeof t>[0])}</h2>
            {job.currentCheckpoint && (
              <p className={styles['heroSubtitle']}>{t(`jobs:stage.${job.currentCheckpoint}` as Parameters<typeof t>[0])}</p>
            )}
          </div>
        </div>
      )}

      <div className={styles['header']}>
        <h3 className={styles['title']}>{t('jobs:progress.title')}</h3>
        <Badge tone={STATUS_TONE[job.status]}>{t(`jobs:status.${job.status}`)}</Badge>
      </div>

      {job.status === 'failed' && (
        <div>
          <p className={styles['attemptNote']}>{t('jobs:progress.attempt', { attempt: job.attempts, max: job.maxAttempts })}</p>
          {attemptsExhausted ? (
            <p role="alert" className={styles['terminalFailed']}>
              {t('jobs:progress.terminalFailed')}
            </p>
          ) : (
            <p className={styles['willRetry']}>{t('jobs:progress.willRetry')}</p>
          )}
        </div>
      )}

      {stages.length > 0 && (
        <ol className={styles['stepper']} aria-label={t('jobs:progress.checkpointsTitle')}>
          {stages.map((stage) => {
            let state: StepState = 'queued';
            if (doneStages.has(stage)) state = 'done';
            else if (stage === job.currentCheckpoint) state = job.status === 'failed' ? 'error' : 'running';
            return (
              <li key={stage} className={styles['step']} data-state={state}>
                <span className={styles['stepDot']} aria-hidden="true" />
                <span>{t(`jobs:stage.${stage}` as Parameters<typeof t>[0])}</span>
                <span aria-hidden="true">—</span> <span>{t(`jobs:step.${state}` as Parameters<typeof t>[0])}</span>
              </li>
            );
          })}
        </ol>
      )}

      <div className={styles['logsSection']}>
        <p className={styles['logsTitle']}>{t('jobs:logs.title')}</p>
        {logs.length === 0 ? (
          <p className={styles['logEmpty']}>{t('jobs:logs.empty')}</p>
        ) : (
          <ul className={styles['logList']} aria-live="polite">
            {logs.map((log) => (
              <li key={log.id} className={styles['logRow']} data-level={logLevelForRow(log)}>
                <span className={styles['logTime']}>{formatDate(locale, log.createdAt, { hour: '2-digit', minute: '2-digit' })}</span>
                <span>{log.message}</span>
              </li>
            ))}
          </ul>
        )}
        {hasMoreEarlierLogs && (
          <Button variant="ghost" size="sm" state={isLoadingEarlierLogs ? 'loading' : 'idle'} onClick={() => void loadEarlierLogs()}>
            {t('jobs:logs.loadMore')}
          </Button>
        )}
        {earlierLogsError && <p className={styles['loadMoreError']}>{t('jobs:logs.loadMoreError')}</p>}
      </div>
    </div>
  );
}
